import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AppCard,
  AppDatePicker,
  AppInput,
  AppNoInfo,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import axios from 'axios';
import { Formik } from 'formik';
import Moment from 'moment';
import ReactDOMServer from 'react-dom/server';
import Translator from '../../../layout/components/helpers/Translator';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { axiosPath, path } from '../../../routing/helpers/router';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import TotalEnergiesRiskSidebar from '../../../layout/screenSpecifics/Modules/TotalEnergiesRisk/Sidebar/TotalEnergiesRiskSidebar';
import minions from '../../../minions/minions';
import * as moduleAction from "../../../store/actions/moduleAction";

export default function ModuleTotalEnergiesRiskActionPlanReviewHomeScreen() {
  const { moduleBridgeHex, reviewHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const moduleData = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.moduleData
  );
  const locale = useSelector((state) => state.global?.urlData?.lang);
  const [loading, setLoading] = useState(false);
  const [review, setReview] = useState();
  const [noYesOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-legalCompliance" name="no" />
      ),
      value: false,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-legalCompliance" name="yes" />
      ),
      value: true,
    },
  ]);

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  // > LOAD REVIEW
  const loadReview = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_totalEnergiesRisk_actionPlan_review_get"
          ),
          {
            moduleBridgeHex,
            reviewHex,
          }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            setReview(data);
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setLoading(false)),
    [moduleBridgeHex, reviewHex]
  );

  useEffect(() => {
    setLoading(true);
    loadReview();
  }, []);
  // < LOAD REVIEW

  // > SAVE
  const handleSave = useCallback(
    async (values) =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_totalEnergiesRisk_actionPlan_review_edit"
          ),
          minions.forms.toFormData({
            moduleBridgeHex,
            reviewHex,
            ...values,
          }),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then(() => {
          if (isMounted.current) {
            navigate(
                path({
                  name: "user_module_totalEnergiesRisk_actionPlan_list",
                  params: { moduleBridgeHex, page: 1, filtersHex: 0 },
                })
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, reviewHex]
  );
  // < SAVE

      // > LOAD MODULE DATA //
      const loadModuleData = useCallback(async () => {
        dispatch(
          moduleAction.fetch(
            axiosPath("api_r_user_module_totalEnergiesRisk_getModuleData"),
            "moduleData",
            {
              moduleBridgeHex,
            }
          )
        );
      }, []);
    
      useEffect(() => {
        loadModuleData();
      }, []);
      // > LOAD MODULE DATA //
    
      // < STORE CALLS
      const loadModulePermissions = useCallback(async () => {
        try {
          await dispatch(
            moduleAction.fetch(
              axiosPath("api_r_user_module_totalEnergiesRisk_getPermissions"),
              "permissions",
              {
                moduleBridgeHex,
                subType: "permissions",
              }
            )
          );
          await dispatch(
            moduleAction.fetch(
              axiosPath("api_r_user_module_totalEnergiesRisk_getPermissions"),
              "userBridge",
              {
                moduleBridgeHex,
                subType: "userBridge",
              }
            )
          );
        } catch (err) {}
      }, [moduleBridgeHex]);
    
      useEffect(() => {
        loadModulePermissions();
      }, [moduleBridgeHex]);
  
    if (!moduleData) return <AppNoInfo />

  return (
    <>
      <TotalEnergiesRiskSidebar />
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: moduleData?.moduleName,
                  route: {
                    name: 'user_module_totalEnergiesRisk_actionPlan_list',
                    params: {
                      moduleBridgeHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                { name: 'Edición de revisión de Plan de acción' },
              ]
            : [{ name: '...' }]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          {loading ? (
            <AppNoInfo minHeight={window.innerHeight - 60} />
          ) : (
            <Formik
              validateOnChange={false}
              validateOnBlur={false}
              initialValues={{
                scheduledReviewDate: review?.scheduledReviewDate ?? '',
                description: review?.description ?? '',
                requireDocumentation: review?.requireDocumentation ?? false,
              }}
              onSubmit={handleSave}
            >
              {({
                setFieldValue,
                handleSubmit,
                values,
                errors,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit}>
                  <AppCard header="Edición de revisión de Plan de acción">
                    <div className="container-fluid">
                      <div className="row">
                        <div className="col-lg-6">
                          <AppInput
                            title="Identificador Plan de acción asociado"
                            value={review?.actionPlanCode || ''}
                            isReadOnly
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppInput
                            title="Identificador revisión Plan de acción"
                            value={review?.code || ''}
                            isReadOnly
                          />
                        </div>
                        <div className="col-lg-12">
                          <AppTextArea
                            title="Descripción de revisión"
                            value={values.description}
                            error={errors.description}
                            onChange={(value) =>
                              setFieldValue('description', value)
                            }
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppDatePicker
                            locale={locale}
                            title="Fecha prevista revisión"
                            value={values.scheduledReviewDate}
                            onChange={(opt) =>
                              setFieldValue('scheduledReviewDate', opt)
                            }
                            minDate={Moment().toDate()}
                            // minDate={Moment().add(1, 'days').toDate()}
                            showYearDropdown
                            showMonthDropdown
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppSingleSelect
                            options={noYesOptions}
                            title="Requiere documentación"
                            value={values?.requireDocumentation}
                            error={errors?.requireDocumentation}
                            onChange={(opt) => {
                              setFieldValue('requireDocumentation', opt);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </AppCard>
                  <div className="row mt-3">
                    <div className="col-12">
                      <button
                        type="submit"
                        className="btn btn-sm btn-success float-right"
                        disabled={isSubmitting}
                      >
                        Editar revisión de Plan de acción
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          )}
        </div>
      </main>
    </>
  );
}
